import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogBody, DialogFooter, DialogTitle, DialogDescription } from '@/components/ui/Dialog';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Icon from '@/components/AppIcon';
import { Switch } from '@/components/ui/Switch';
import type { Result } from '../../../../../models/general';

interface LifecycleTypeItem {
  id: number;
  name: string;
  isActive: boolean;
  isResignation?: boolean | null;
  companyId: number;
}

interface LifecycleTypeInput {
  id?: number;
  name: string;
  isActive: boolean;
  isResignation?: boolean | null;
}

// Optional tri-state classification (voluntary/involuntary), enabled only for Exit Reasons.
interface ClassificationConfig {
  label: string;
  helper: string;
  unspecifiedLabel: string;
  resignationLabel: string;
  terminationLabel: string;
  resignationChip: string;
  terminationChip: string;
}

interface LifecycleTypeTabProps {
  title: string;
  description: string;
  emptyMessage: string;
  emptyIcon: string;
  addLabel: string;
  editLabel: string;
  deleteLabel: string;
  nameLabel: string;
  namePlaceholder: string;
  successAddMessage: string;
  successUpdateMessage: string;
  successDeleteMessage: string;
  failAddMessage: string;
  failDeleteMessage: string;
  deleteConfirmMessage: string;
  duplicateMessage: string;
  fetchItems: () => Promise<Result<LifecycleTypeItem[]>>;
  addItem: (input: LifecycleTypeInput) => Promise<Result<LifecycleTypeItem>>;
  updateItem: (id: number, input: LifecycleTypeInput) => Promise<Result<LifecycleTypeItem>>;
  deleteItem: (id: number) => Promise<Result<boolean>>;
  testIdPrefix: string;
  classification?: ClassificationConfig;
}

const LifecycleTypeTab: React.FC<LifecycleTypeTabProps> = ({
  title, description, emptyMessage, emptyIcon, addLabel, editLabel, deleteLabel,
  nameLabel, namePlaceholder, successAddMessage, successUpdateMessage, successDeleteMessage,
  failAddMessage, failDeleteMessage, deleteConfirmMessage, duplicateMessage,
  fetchItems, addItem, updateItem, deleteItem, testIdPrefix, classification,
}) => {
  const { t } = useTranslation();
  const [items, setItems] = useState<LifecycleTypeItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState<LifecycleTypeItem | null>(null);
  const [formName, setFormName] = useState('');
  const [formIsActive, setFormIsActive] = useState(true);
  const [formIsResignation, setFormIsResignation] = useState<boolean | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<LifecycleTypeItem | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const loadItems = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await fetchItems();
      if (result?.errors?.length) {
        setError(result.errors[0]?.message || t('Failed to load data'));
      } else {
        setError(null);
        setItems(result.payload ?? []);
      }
    } finally {
      setIsLoading(false);
    }
  }, [fetchItems, t]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 5000);
  };

  const resetForm = () => {
    setShowModal(false);
    setEditingItem(null);
    setFormName('');
    setFormIsActive(true);
    setFormIsResignation(null);
  };

  const handleEdit = (item: LifecycleTypeItem) => {
    setEditingItem(item);
    setFormName(item.name);
    setFormIsActive(item.isActive);
    setFormIsResignation(item.isResignation ?? null);
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formName.trim()) return;

    const isDuplicate = items.some(
      i => i.name.toLowerCase() === formName.trim().toLowerCase() && i.id !== editingItem?.id
    );
    if (isDuplicate) {
      showToast(duplicateMessage, 'error');
      return;
    }

    setIsSaving(true);
    try {
      const input: LifecycleTypeInput = {
        name: formName.trim(),
        isActive: formIsActive,
        ...(classification ? { isResignation: formIsResignation } : {}),
      };

      if (editingItem) {
        const result = await updateItem(editingItem.id, input);
        if (!result?.errors?.length) {
          await loadItems();
          resetForm();
          showToast(successUpdateMessage, 'success');
        } else {
          showToast(result.errors[0]?.message || failAddMessage, 'error');
        }
      } else {
        const result = await addItem(input);
        if (!result?.errors?.length) {
          await loadItems();
          resetForm();
          showToast(successAddMessage, 'success');
        } else {
          showToast(result.errors[0]?.message || failAddMessage, 'error');
        }
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      const result = await deleteItem(deleteTarget.id);
      if (!result?.errors?.length) {
        setItems(items.filter(i => i.id !== deleteTarget.id));
        setDeleteTarget(null);
        showToast(successDeleteMessage, 'success');
      } else {
        setDeleteTarget(null);
        showToast(result.errors[0]?.message || failDeleteMessage, 'error');
      }
    } catch {
      setDeleteTarget(null);
      showToast(failDeleteMessage, 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  const toggleStatus = async (item: LifecycleTypeItem) => {
    const result = await updateItem(item.id, {
      name: item.name,
      isActive: !item.isActive,
      // Preserve the classification on a status-only toggle (update sends the full record).
      ...(classification ? { isResignation: item.isResignation ?? null } : {}),
    });
    if (!result?.errors?.length) {
      setItems(items.map(i => i.id === item.id ? { ...i, isActive: !i.isActive } : i));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold text-foreground">{title}</h3>
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>
        <Button variant="default" iconName="Plus" onClick={() => setShowModal(true)} data-testid={`${testIdPrefix}-add-btn`}>
          {addLabel}
        </Button>
      </div>

      {error && (
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center space-x-2 rtl:space-x-reverse">
            <Icon name="AlertCircle" size={16} className="text-destructive" />
            <span className="text-sm text-destructive">{error}</span>
          </div>
          <Button variant="outline" size="sm" onClick={loadItems}>{t('Retry')}</Button>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-12">
          <Icon name={emptyIcon} size={48} className="text-muted-foreground/50 mx-auto mb-4" />
          <p className="text-muted-foreground">{emptyMessage}</p>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {items.map((item) => (
            <div key={item.id} className="bg-card border border-border rounded-lg p-5 hover:shadow-md transition-shadow" data-testid={`${testIdPrefix}-card`}>
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-foreground">{item.name}</h4>
                <Switch
                  checked={item.isActive}
                  onCheckedChange={() => toggleStatus(item)}
                  data-testid={`${testIdPrefix}-toggle-status`}
                />
              </div>
              <div className="mb-3 flex flex-wrap items-center gap-2">
                <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                  item.isActive ? 'bg-success/10 text-success' : 'bg-muted text-muted-foreground'
                }`}>
                  {item.isActive ? t('Active') : t('Inactive')}
                </span>
                {classification && item.isResignation != null && (
                  <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                    item.isResignation ? 'bg-primary/10 text-primary' : 'bg-warning/10 text-warning'
                  }`}>
                    {item.isResignation ? classification.resignationChip : classification.terminationChip}
                  </span>
                )}
              </div>
              <div className="flex items-center justify-between pt-3 border-t border-border">
                <Button variant="ghost" size="sm" iconName="Edit" onClick={() => handleEdit(item)} data-testid={`${testIdPrefix}-edit-btn`}>
                  {t('Edit')}
                </Button>
                <Button variant="ghost" size="sm" iconName="Trash2" onClick={() => setDeleteTarget(item)} className="text-destructive hover:text-destructive" data-testid={`${testIdPrefix}-delete-btn`}>
                  {t('Delete')}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <DialogContent size="sm" data-testid={`${testIdPrefix}-delete-dialog`}>
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center">
                <Icon name="AlertTriangle" size={20} className="text-destructive" />
              </div>
              <DialogTitle>{deleteLabel}</DialogTitle>
            </div>
          </DialogHeader>
          <DialogBody>
            <DialogDescription>
              {deleteConfirmMessage} <span className="font-medium text-foreground">{deleteTarget?.name}</span>?
            </DialogDescription>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)} disabled={isDeleting} data-testid={`${testIdPrefix}-delete-cancel`}>
              {t('Cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={isDeleting}
              loading={isDeleting}
              data-testid={`${testIdPrefix}-delete-confirm`}
            >
              {t('Delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-5 py-3 rounded-lg shadow-lg text-sm font-medium flex items-center gap-2 transition-all ${
          toast.type === 'success' ? 'bg-success text-success-foreground' : 'bg-destructive text-destructive-foreground'
        }`}>
          <Icon name={toast.type === 'success' ? 'CheckCircle' : 'AlertCircle'} size={16} />
          {toast.message}
          <button onClick={() => setToast(null)} className="ms-3 hover:opacity-80">
            <Icon name="X" size={14} />
          </button>
        </div>
      )}

      {/* Add/Edit Modal */}
      <Dialog open={showModal} onOpenChange={(open) => { if (!open) resetForm(); }}>
        <DialogContent size="md" data-testid={`${testIdPrefix}-modal`}>
          <DialogHeader>
            <DialogTitle>{editingItem ? editLabel : addLabel}</DialogTitle>
          </DialogHeader>
          <DialogBody className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">{nameLabel}</label>
              <Input
                type="text"
                value={formName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormName(e.target.value)}
                placeholder={namePlaceholder}
                data-testid={`${testIdPrefix}-name-input`}
              />
            </div>
            {classification && (
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">{classification.label}</label>
                <div className="grid grid-cols-3 gap-2" role="radiogroup" aria-label={classification.label}>
                  {([
                    { v: null as boolean | null, label: classification.unspecifiedLabel, key: 'unspecified' },
                    { v: true as boolean | null, label: classification.resignationLabel, key: 'resignation' },
                    { v: false as boolean | null, label: classification.terminationLabel, key: 'termination' },
                  ]).map((opt) => {
                    const selected = formIsResignation === opt.v;
                    return (
                      <button
                        key={opt.key}
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        onClick={() => setFormIsResignation(opt.v)}
                        className={`rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                          selected
                            ? 'border-primary bg-primary/10 text-primary'
                            : 'border-border bg-card text-muted-foreground hover:border-primary/40 hover:text-foreground'
                        }`}
                        data-testid={`${testIdPrefix}-classification-${opt.key}`}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-muted-foreground mt-2">{classification.helper}</p>
              </div>
            )}
            <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
              <div>
                <label className="text-sm font-medium text-foreground">{t('Active')}</label>
                <p className="text-xs text-muted-foreground">{t('Enable or disable this item')}</p>
              </div>
              <Switch
                checked={formIsActive}
                onCheckedChange={setFormIsActive}
                data-testid={`${testIdPrefix}-active-toggle`}
              />
            </div>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={resetForm} data-testid={`${testIdPrefix}-modal-cancel`}>{t('Cancel')}</Button>
            <Button
              variant="default"
              onClick={handleSave}
              disabled={!formName.trim() || isSaving}
              data-testid={`${testIdPrefix}-save-btn`}
            >
              {isSaving ? t('Saving...') : editingItem ? t('Update') : t('Add')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LifecycleTypeTab;
